import type { ReactNode } from 'react';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import type { AzureConfig } from '../../services/versionService';

type Category = 'compute' | 'storage' | 'integration' | 'search' | 'external';

interface ArchitectureDiagramProps {
  azureConfig: AzureConfig | null;
}

const useStyles = makeStyles({
  section: {
    marginTop: '36px',
    paddingTop: '28px',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
  title: {
    margin: '0 0 4px 0',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    margin: '0 0 24px 0',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    maxWidth: '900px',
  },
  legend: {
    display: 'flex',
    gap: '18px',
    flexWrap: 'wrap',
    marginBottom: '24px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
  },
  legendSwatchCompute: { borderLeftColor: tokens.colorPaletteBlueBorderActive },
  legendSwatchStorage: { borderLeftColor: tokens.colorPaletteDarkOrangeBorderActive },
  legendSwatchIntegration: { borderLeftColor: tokens.colorPalettePurpleBorderActive },
  legendSwatchSearch: { borderLeftColor: tokens.colorPaletteGreenBorderActive },
  legendSwatchExternal: {
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground3,
  },

  canvas: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
    padding: '28px 24px 32px',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },

  layer: {
    marginBottom: '28px',
    ':last-child': { marginBottom: 0 },
  },
  layerLabel: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '::after': {
      content: '""',
      flex: 1,
      height: '1px',
      backgroundColor: tokens.colorNeutralStroke2,
      marginLeft: '8px',
    },
  },

  rowIngest: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1.8fr',
    gap: '12px',
    alignItems: 'center',
    maxWidth: '760px',
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  rowProcess: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1.4fr 1fr 1fr',
    gap: '14px',
    alignItems: 'stretch',
    '@media (max-width: 1100px)': {
      gridTemplateColumns: '1fr 1fr',
    },
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  rowData: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '14px',
    alignItems: 'stretch',
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  rowExternal: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '14px',
    alignItems: 'stretch',
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },

  arrowRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '6px 0',
  },
  arrowIco: {
    color: tokens.colorNeutralStroke1,
    fontSize: '18px',
    lineHeight: 1,
  },
  arrowLabel: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '0 6px',
  },
  inlineArrow: {
    color: tokens.colorNeutralStroke1,
    fontSize: '22px',
    textAlign: 'center',
    userSelect: 'none',
    '@media (max-width: 720px)': { display: 'none' },
  },

  res: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRadius: '4px',
    padding: '12px 14px',
    textDecorationLine: 'none',
    color: 'inherit',
    display: 'block',
    boxShadow: tokens.shadow2,
    transitionProperty: 'box-shadow, transform, border-color',
    transitionDuration: '120ms',
    transitionTimingFunction: 'ease',
    position: 'relative',
  },
  resLink: {
    cursor: 'pointer',
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-1px)',
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
    },
  },
  resCompute: { borderLeftColor: tokens.colorPaletteBlueBorderActive },
  resStorage: { borderLeftColor: tokens.colorPaletteDarkOrangeBorderActive },
  resIntegration: { borderLeftColor: tokens.colorPalettePurpleBorderActive },
  resSearch: { borderLeftColor: tokens.colorPaletteGreenBorderActive },
  resExternal: {
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
    borderLeftWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'default',
  },
  resKind: {
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
  },
  resName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.25',
    marginTop: '4px',
    marginBottom: '4px',
  },
  resId: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: '11.5px',
    color: tokens.colorNeutralForeground2,
    marginTop: '6px',
    wordBreak: 'break-all',
  },
  resMeta: {
    fontSize: '11.5px',
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
  },

  fnList: {
    listStyleType: 'none',
    margin: '10px 0 0 0',
    padding: '8px 0 0 0',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
  fnItem: { margin: 0, padding: 0 },
  fnLink: {
    display: 'block',
    textDecorationLine: 'none',
    color: tokens.colorNeutralForeground1,
    fontSize: '12.5px',
    padding: '6px 8px',
    borderRadius: '3px',
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
    margin: '3px 0',
    transitionProperty: 'background-color, border-color',
    transitionDuration: '120ms',
    transitionTimingFunction: 'ease',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderLeftColor: tokens.colorPaletteBlueBorderActive,
    },
  },
  fnName: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    display: 'block',
  },
  fnDesc: {
    color: tokens.colorNeutralForeground2,
    fontSize: '11.5px',
    lineHeight: '1.35',
    marginTop: '2px',
    display: 'block',
  },

  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    marginTop: '14px',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footerNote: {
    marginTop: '24px',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
});

function buildPortalUrl(config: AzureConfig | null, providerAndType: string, resourceName: string, suffix = '/overview'): string | undefined {
  if (!config || !config.tenantId || !config.subscriptionId || !config.resourceGroup) {
    return undefined;
  }
  return `https://portal.azure.com/#@${config.tenantId}/resource/subscriptions/${config.subscriptionId}/resourceGroups/${config.resourceGroup}/providers/${providerAndType}/${resourceName}${suffix}`;
}

interface ResourceBoxProps {
  category: Category;
  kind: string;
  name: string;
  resourceId?: string;
  meta?: ReactNode;
  href?: string;
  children?: ReactNode;
}

function ResourceBox({ category, kind, name, resourceId, meta, href, children }: ResourceBoxProps) {
  const styles = useStyles();
  const categoryClass = {
    compute: styles.resCompute,
    storage: styles.resStorage,
    integration: styles.resIntegration,
    search: styles.resSearch,
    external: styles.resExternal,
  }[category];

  const className = `${styles.res} ${categoryClass} ${href ? styles.resLink : ''}`.trim();

  const content = (
    <>
      <div className={styles.resKind}>{kind}</div>
      <div className={styles.resName}>{name}</div>
      {resourceId && <div className={styles.resId}>{resourceId}</div>}
      {meta && <div className={styles.resMeta}>{meta}</div>}
      {children}
    </>
  );

  if (href) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }
  return <div className={className}>{content}</div>;
}

interface FunctionItemProps {
  name: string;
  description: ReactNode;
  href?: string;
}

function FunctionItem({ name, description, href }: FunctionItemProps) {
  const styles = useStyles();
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (href) {
    return (
      <li className={styles.fnItem}>
        <a className={styles.fnLink} href={href} target="_blank" rel="noopener noreferrer" onClick={stop}>
          <span className={styles.fnName}>{name}</span>
          <span className={styles.fnDesc}>{description}</span>
        </a>
      </li>
    );
  }
  return (
    <li className={styles.fnItem}>
      <div className={styles.fnLink} style={{ cursor: 'default' }}>
        <span className={styles.fnName}>{name}</span>
        <span className={styles.fnDesc}>{description}</span>
      </div>
    </li>
  );
}

const SITE_PROVIDER = 'Microsoft.Web/sites';
const SB_PROVIDER = 'Microsoft.ServiceBus/namespaces';
const STORAGE_PROVIDER = 'Microsoft.Storage/storageAccounts';
const SEARCH_PROVIDER = 'Microsoft.Search/searchServices';

export default function ArchitectureDiagram({ azureConfig }: ArchitectureDiagramProps) {
  const styles = useStyles();
  const cfg = azureConfig;

  const fnUrl = (app: string, fn: string) =>
    cfg && cfg.tenantId && cfg.subscriptionId && cfg.resourceGroup
      ? `https://portal.azure.com/#@${cfg.tenantId}/resource/subscriptions/${cfg.subscriptionId}/resourceGroups/${cfg.resourceGroup}/providers/${SITE_PROVIDER}/${app}/functions/${fn}`
      : undefined;

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>Arquitetura da Plataforma</h2>
      <p className={styles.subtitle}>
        Como os projetos do SSDS2 se relacionam em produção. Clique em qualquer recurso para abri-lo no Azure Portal.
      </p>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchCompute}`} /> App / Function App
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchIntegration}`} /> Mensageria
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchStorage}`} /> Armazenamento
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchSearch}`} /> Busca
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchExternal}`} /> Recurso externo
        </span>
      </div>

      <div className={styles.canvas}>

        {/* CAMADA 1 — INGESTÃO */}
        <div className={styles.layer}>
          <div className={styles.layerLabel}>Camada 1 — Ingestão</div>
          <div className={styles.rowIngest}>
            <ResourceBox
              category="external"
              kind="Sistema externo"
              name="SIS Senac"
              meta="Sistema de origem · publica eventos de matrícula, turmas e usuários na fila"
            />
            <div className={styles.inlineArrow} aria-hidden="true">→</div>
            <ResourceBox
              category="integration"
              kind="Service Bus Namespace"
              name="Service Bus"
              resourceId="sen-prd-brs-bus-rg-02"
              meta={<>queue: <code>sen-prd-brs-que-sds</code></>}
              href={buildPortalUrl(cfg, SB_PROVIDER, 'sen-prd-brs-bus-rg-02')}
            />
          </div>
        </div>

        <div className={styles.arrowRow} aria-hidden="true">
          <span className={styles.arrowIco}>↓</span>
          <span className={styles.arrowLabel}>consumido por</span>
          <span className={styles.arrowIco}>↓</span>
        </div>

        {/* CAMADA 2 — PROCESSAMENTO */}
        <div className={styles.layer}>
          <div className={styles.layerLabel}>Camada 2 — Processamento</div>
          <div className={styles.rowProcess}>

            <ResourceBox
              category="compute"
              kind="Function App"
              name="Receiver"
              resourceId="sen-prd-brs-fcn-svc-02"
              meta="Consome a fila do SIS, sincroniza com Microsoft Graph, gera alertas e snapshots da fila."
              href={buildPortalUrl(cfg, SITE_PROVIDER, 'sen-prd-brs-fcn-svc-02')}
            >
              <ul className={styles.fnList}>
                <FunctionItem
                  name="ServiceBusTriggerFunction"
                  description="Consome mensagens da queue → Workers → Microsoft Graph; grava log em Table Storage + Search; encaminha falhas para DLQ."
                  href={fnUrl('sen-prd-brs-fcn-svc-02', 'ServiceBusTriggerFunction')}
                />
                <FunctionItem
                  name="QueueSnapshotFunction"
                  description={<>Timer horário (+ HTTP manual). Captura DLQ/Scheduled e persiste em <code>QueueSnapshots</code> (Blob) e <code>QueueMetricsHistory</code> (Table).</>}
                  href={fnUrl('sen-prd-brs-fcn-svc-02', 'QueueSnapshotFunction')}
                />
                <FunctionItem
                  name="AlertProcessorFunction"
                  description="Avalia thresholds configurados (DLQ, falhas por entidade) e dispara e-mails de alerta via SMTP."
                  href={fnUrl('sen-prd-brs-fcn-svc-02', 'AlertProcessorFunction')}
                />
              </ul>
            </ResourceBox>

            <ResourceBox
              category="compute"
              kind="Function App"
              name="BackgroundJobs"
              resourceId="sen-prd-brs-fcn-svc-03"
              meta="Agregação periódica de métricas, limpeza de DLQ e retenção de dados."
              href={buildPortalUrl(cfg, SITE_PROVIDER, 'sen-prd-brs-fcn-svc-03')}
            >
              <ul className={styles.fnList}>
                <FunctionItem
                  name="DailyMetricsAggregationFunction"
                  description={<>Timer @10min. Consolida métricas horárias (<code>QueueMetricsHistory</code>) em diárias (<code>DailyQueueMetrics</code>).</>}
                  href={fnUrl('sen-prd-brs-fcn-svc-03', 'DailyMetricsAggregationFunction')}
                />
                <FunctionItem
                  name="OperationsAggregationFunction"
                  description={<>Timer @05min + <em>RunOnStartup</em>. Agrega operações do Search (10k batch) em <code>OperationsMetricsHistory</code>/<code>Daily</code>; faz backfill histórico.</>}
                  href={fnUrl('sen-prd-brs-fcn-svc-03', 'OperationsAggregationFunction')}
                />
                <FunctionItem
                  name="DlqCleanupFunction"
                  description="Timer. Expurga mensagens da DLQ com mais de 5 dias (configurável)."
                  href={fnUrl('sen-prd-brs-fcn-svc-03', 'DlqCleanupFunction')}
                />
                <FunctionItem
                  name="DataRetentionFunction"
                  description="Timer. Remove documentos do Search e linhas do Table Storage acima do TTL (30/60 dias)."
                  href={fnUrl('sen-prd-brs-fcn-svc-03', 'DataRetentionFunction')}
                />
              </ul>
            </ResourceBox>

            <ResourceBox
              category="compute"
              kind="App Service"
              name="API"
              resourceId="sen-prd-brs-api-svc-02"
              meta="REST: Education, Dashboard, Logs, DLQ. Lê de Table/Blob Storage e Azure AI Search; consulta Microsoft Graph quando necessário."
              href={buildPortalUrl(cfg, SITE_PROVIDER, 'sen-prd-brs-api-svc-02')}
            />

            <ResourceBox
              category="compute"
              kind="App Service"
              name="Monitor"
              resourceId="sen-prd-brs-mon-svc-02"
              meta="Frontend React + Fluent UI v9. Consome exclusivamente a API REST."
              href={buildPortalUrl(cfg, SITE_PROVIDER, 'sen-prd-brs-mon-svc-02')}
            />

          </div>
        </div>

        <div className={styles.arrowRow} aria-hidden="true">
          <span className={styles.arrowIco}>↓</span>
          <span className={styles.arrowLabel}>leitura / escrita</span>
          <span className={styles.arrowIco}>↓</span>
        </div>

        {/* CAMADA 3 — DADOS */}
        <div className={styles.layer}>
          <div className={styles.layerLabel}>Camada 3 — Armazenamento &amp; Busca</div>
          <div className={styles.rowData}>
            <ResourceBox
              category="storage"
              kind="Storage Account · Tables"
              name="Table Storage"
              resourceId="stssds20prdbrs01"
              meta="Logs, métricas, configurações de filas/alertas/retenção, snapshots agregados."
              href={buildPortalUrl(cfg, STORAGE_PROVIDER, 'stssds20prdbrs01')}
            />
            <ResourceBox
              category="storage"
              kind="Storage Account · Blobs"
              name="Blob Storage"
              resourceId="stssds20prdbrs02"
              meta={<>Containers <code>event-payloads</code> (payloads &gt;32KB) e <code>queue-snapshots</code>.</>}
              href={buildPortalUrl(cfg, STORAGE_PROVIDER, 'stssds20prdbrs02')}
            />
            <ResourceBox
              category="search"
              kind="Azure AI Search"
              name="Search Service"
              resourceId="search-ssds20-prd-brs-01"
              meta={<>Índice <code>education-logs-index-v2</code> · busca full-text de logs e operações.</>}
              href={buildPortalUrl(cfg, SEARCH_PROVIDER, 'search-ssds20-prd-brs-01')}
            />
          </div>
        </div>

        {/* EXTERNOS */}
        <div className={styles.layer}>
          <div className={styles.layerLabel}>Recursos Externos &amp; Identidade</div>
          <div className={styles.rowExternal}>
            <ResourceBox
              category="external"
              kind="API externa Microsoft"
              name="Microsoft Graph"
              meta="Education API: Schools, Classes, Users, Teams. Usado por Receiver (sync) e API (consultas)."
              href="https://developer.microsoft.com/en-us/graph/graph-explorer"
            />
            <ResourceBox
              category="external"
              kind="Identidade · Microsoft Entra"
              name="Microsoft Entra ID"
              resourceId="senacsp.edu.br"
              meta="Autenticação de usuários (Monitor) e App Registration (API → Microsoft Graph)."
              href={cfg && cfg.tenantId ? `https://entra.microsoft.com/#@${cfg.tenantId}/` : undefined}
            />
            <ResourceBox
              category="external"
              kind="Sistema externo"
              name="SIS Senac"
              meta="Sistema acadêmico de origem dos eventos. (Sem recurso correspondente no Azure.)"
            />
          </div>
        </div>

        <Text className={styles.hint}>
          Passe o mouse sobre as Functions individuais para destacá-las · clique para abrir direto na página da Function no Azure Portal.
        </Text>
      </div>

      <div className={styles.footerNote}>
        <strong>Fluxo resumido:</strong>{' '}
        SIS publica eventos no <em>Service Bus</em> →{' '}
        <em>Receiver/ServiceBusTriggerFunction</em> consome, chama <em>Microsoft Graph</em> e grava log em <em>Table Storage</em> + <em>Azure AI Search</em> →{' '}
        <em>BackgroundJobs</em> agrega métricas e mantém retenção →{' '}
        <em>API</em> serve dados consolidados para o <em>Monitor</em>, autenticando via <em>Microsoft Entra ID</em>.
      </div>
    </section>
  );
}
